import { useState, useEffect } from "react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { UserPlus, Shield, Eye, Edit3, UserX, Check, Copy } from "lucide-react";
import { toast } from "sonner";

const roleBadge = {
  admin: { bg: "rgba(30,58,95,0.12)", color: "#1E3A5F" },
  editor: { bg: "rgba(124,58,237,0.12)", color: "#7C3AED" },
  viewer: { bg: "rgba(231,111,81,0.12)", color: "#E76F51" },
};

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "viewer" });
  const [inviteResult, setInviteResult] = useState(null);
  const isAdmin = currentUser?.role === "admin";

  const fetchUsers = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); } catch (err) { console.error(err); } finally { setLoading(false); }
  };
  useEffect(() => { fetchUsers(); }, []);

  const handleInvite = async () => {
    try {
      const { data } = await api.post("/admin/users/invite", inviteForm);
      setInviteResult(data);
      fetchUsers();
      toast.success(`Invited ${data.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to invite"); }
  };

  const handleUpdateRole = async (userId, role) => {
    try {
      await api.patch(`/admin/users/${userId}`, { role });
      fetchUsers();
      setEditUser(null);
      toast.success("Role updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const handleDeactivate = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      fetchUsers();
      toast.success("User deactivated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-6 space-y-4" data-testid="users-loading"><Skeleton className="h-8 w-48 bg-white/5" />{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 bg-white/5" />)}</div>;

  return (
    <div className="p-6 space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">User Management</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{users.length} users in organization</p>
        </div>
        {isAdmin && (
          <Button className="rounded-lg font-semibold text-sm border-0 text-[#2D3142]" style={{ background: "linear-gradient(135deg, #29B5E8 0%, #1B8FC2 100%)" }} onClick={() => { setShowInvite(true); setInviteResult(null); setInviteForm({ email: "", name: "", role: "viewer" }); }} data-testid="invite-user-button">
            <UserPlus className="w-4 h-4 mr-2" /> Invite User
          </Button>
        )}
      </div>

      <div className="sf-card overflow-hidden" data-testid="users-table">
        <Table className="data-table">
          <TableHeader>
            <TableRow className="border-b" style={{ borderColor: "rgba(30,58,95,0.06)" }}>
              <TableHead>User</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Joined</TableHead><TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const rb = roleBadge[u.role] || roleBadge.viewer;
              return (
                <TableRow key={u._id} className="border-b hover:bg-gray-50 transition-colors" style={{ borderColor: "rgba(255,255,255,0.03)" }} data-testid={`user-row-${u._id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold text-[#2D3142]" style={{ background: rb.bg, color: rb.color }}>{u.name?.charAt(0)?.toUpperCase() || "?"}</div>
                      <span className="text-[13px] font-medium text-[#C0CDD8]">{u.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="mono-data" style={{ color: "#6B7280" }}>{u.email}</TableCell>
                  <TableCell>
                    <Badge className="text-[9px] uppercase font-bold border-0 rounded-md" style={{ background: rb.bg, color: rb.color }}>{u.role}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className="text-[9px] uppercase font-bold border-0 rounded-md" style={{ background: u.is_active === false ? "rgba(190,52,85,0.12)" : "rgba(45,106,79,0.12)", color: u.is_active === false ? "#F87171" : "#4ADE80" }}>{u.is_active === false ? "Inactive" : "Active"}</Badge>
                  </TableCell>
                  <TableCell className="mono-data text-[11px]" style={{ color: "#9CA3AF" }}>{u.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>
                    {isAdmin && u.email !== currentUser?.email && (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-[#5A6B82] hover:text-[#29B5E8] hover:bg-[#29B5E8]/10" onClick={() => setEditUser(u)} data-testid={`edit-user-${u._id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                        {u.is_active !== false && <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-[#5A6B82] hover:text-[#EF4444] hover:bg-[#EF4444]/10" onClick={() => handleDeactivate(u._id)} data-testid={`deactivate-user-${u._id}`}><UserX className="w-3.5 h-3.5" /></Button>}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="sm:max-w-[440px]" style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }} data-testid="invite-dialog">
          <DialogHeader><DialogTitle className="text-[#2D3142]">Invite New User</DialogTitle></DialogHeader>
          {!inviteResult ? (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Email</Label>
                <Input value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="user@company.io" className="h-10 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="invite-email-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Full Name</Label>
                <Input value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="Full name" className="h-10 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="invite-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Role</Label>
                <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                  <SelectTrigger className="h-10 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }}>
                    <SelectItem value="admin"><div className="flex items-center gap-2"><Shield className="w-3 h-3" style={{ color: "#1E3A5F" }} /> Admin</div></SelectItem>
                    <SelectItem value="editor"><div className="flex items-center gap-2"><Edit3 className="w-3 h-3" style={{ color: "#7C3AED" }} /> Editor</div></SelectItem>
                    <SelectItem value="viewer"><div className="flex items-center gap-2"><Eye className="w-3 h-3" style={{ color: "#E76F51" }} /> Viewer</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button className="rounded-lg font-semibold text-sm border-0 text-[#2D3142]" style={{ background: "linear-gradient(135deg, #29B5E8 0%, #1B8FC2 100%)" }} onClick={handleInvite} data-testid="send-invite-button">Send Invite</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-2" data-testid="invite-success">
              <div className="p-4 rounded-lg" style={{ background: "rgba(45,106,79,0.06)", border: "1px solid rgba(45,106,79,0.15)" }}>
                <div className="flex items-center gap-2 mb-2"><Check className="w-4 h-4" style={{ color: "#2D6A4F" }} /><span className="font-semibold text-[#2D3142]">User Invited</span></div>
                <p className="text-[13px]" style={{ color: "#6B7280" }}>Share these credentials with {inviteResult.name}:</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: "rgba(45,49,66,0.03)" }}>
                  <div><span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Email</span><p className="text-[13px] font-mono text-[#2D3142]">{inviteResult.email}</p></div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: "rgba(45,49,66,0.03)" }}>
                  <div><span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Temp Password</span><p className="text-[13px] font-mono text-[#2D3142]">{inviteResult.temp_password}</p></div>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(inviteResult.temp_password); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" style={{ color: "#1E3A5F" }} /></Button>
                </div>
              </div>
              <DialogFooter><Button variant="outline" className="rounded-lg border-gray-200" onClick={() => setShowInvite(false)}>Done</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="sm:max-w-[380px]" style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }} data-testid="edit-role-dialog">
          <DialogHeader><DialogTitle className="text-[#2D3142]">Change Role: {editUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {["admin", "editor", "viewer"].map((role) => (
              <button key={role} onClick={() => handleUpdateRole(editUser?._id, role)} className="w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors hover:bg-black/[0.02]" style={{ border: editUser?.role === role ? "1px solid rgba(30,58,95,0.3)" : "1px solid rgba(255,255,255,0.05)", background: editUser?.role === role ? "rgba(30,58,95,0.06)" : "transparent" }} data-testid={`set-role-${role}`}>
                <div className="flex items-center gap-3">
                  {role === "admin" ? <Shield className="w-4 h-4" style={{ color: "#1E3A5F" }} /> : role === "editor" ? <Edit3 className="w-4 h-4" style={{ color: "#7C3AED" }} /> : <Eye className="w-4 h-4" style={{ color: "#E76F51" }} />}
                  <div>
                    <span className="text-[13px] font-medium text-[#2D3142] capitalize">{role}</span>
                    <p className="text-[11px]" style={{ color: "#6B7280" }}>{role === "admin" ? "Full access, manage users & projects" : role === "editor" ? "Create & edit pipelines, approve remediations" : "Read-only access to all data"}</p>
                  </div>
                </div>
                {editUser?.role === role && <Check className="w-4 h-4" style={{ color: "#1E3A5F" }} />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
